import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Timeline } from 'antd-v5';
import {
  PurchaseOutline,
  MassMessages,
  ClientActivity,
} from '../../../../../componentsV2/Layout/Report/components/Icons';
import { getRequest } from '../../../../../core/apiRequests';
import { CalendarOutlined, BulbOutlined, BellOutlined } from '@ant-design/icons';
import TimelineItem from '../../../../../componentsV2/Layout/Report/components/Timeline';
import ReminderItem from '../../../../../componentsV2/Layout/Report/components/ReminderTimelineItem';
import SalesTimelineItem from '../../../../../componentsV2/Layout/Report/components/SalesTimelineItem';

const SalesExpandRow = ({ activities, transactions, client_name, licenseKey, sales_transaction_date, ...rest }) => {
  const [loading, setLoading] = useState(true);
  const [activitiesData, setActivitiesData] = useState([]);
  const [salesTransactionData, setSalesTransactionData] = useState([]);

  const getReportAttributedData = useCallback(() => {
    setLoading(true);
    const reqObj = {
      params: ['report', 'attributedMeta'],
      query: {
        reminders: JSON.stringify(activities.reminders?.map(item => item.id)),
        chatMessages: JSON.stringify(activities.chatMessages?.map(item => item.id)),
        salesTransactions: JSON.stringify(transactions.map(item => Number(item.sales_transaction_id) || 0)),
        clientActivity: JSON.stringify(activities.clientActivity?.map(item => item.id)),
        // Removed appointments from request - we already have this data
        licenseKey: licenseKey,
      },
      loadingStatusOff: true,
    };
    getRequest(reqObj)
      .then(res => {
        // Inject appointment data to simulate attributedMeta response
        const appointmentData = activities.appointments?.map(appointment => ({
          id: appointment.id,
          type: 'appointment',
          client_name: client_name,
          ...appointment
        })) || [];
        
        // Combine real response with appointment data
        const combinedResponse = [...res, ...appointmentData];
        
        const sales = combinedResponse
          .filter(item => item.type === 'salesTransaction')
          .map(sale => {
            const findTransaction = transactions.find(transaction => transaction.sales_transaction_id === sale.id);
            if (findTransaction) {
              return {
                ...sale,
                ...findTransaction,
              };
            }
            return { ...sale };
          });
        const acts = combinedResponse
          .filter(item => item.type !== 'salesTransaction')
          .map(act => {
            let type = act.type === 'chatMessage' ? 'chatMessages' : act.type;
            type = (type === 'reminder') ? 'reminders' : type;
            type = (type === 'appointment') ? 'appointments' : type;
            // For reminders, the act.id might be under a different field name
            const actId = type === 'reminders' ? (act.reminder_id || act.id) : act.id;
            const findActivity = activities[type]?.find(activity => activity.id == actId);
            if (findActivity) {
              return {
                ...findActivity,
                ...act,
                iconType: act.type,
              };
            }
            return { ...act, iconType: act.type };
          });
        setSalesTransactionData(sales);
        setActivitiesData(acts);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [activities]);

  useEffect(() => {
    getReportAttributedData();
  }, [activities]);

  const itemSwitch = activity => {
    const { type } = activity;
    switch (type) {
      case 'reminder':
        return <ReminderItem {...activity} />;
      case 'chatMessages':
        return <MessageItem {...activity} />;
      case 'clientActivity':
        return <ClientActivityItem {...activity} />;
      case 'appointments':
        return <AppointmentItem {...activity} />;
      default:
        return <MessageItem {...activity} />;
    }
  };

  return (
    <div className="m-left-23">
      <div className="flex-row-spacebetween-nowrap">
        <Timeline
          pending={loading ? 'Loading...' : null}
          items={[
            ...salesTransactionData.map(({ sales_transaction_amount, description }) => ({
              children: (
                <SalesTimelineItem
                  client_name={client_name}
                  sales_transaction_amount={sales_transaction_amount}
                  description={description}
                  activity_date={sales_transaction_date}
                />
              ),
              dot: <PurchaseOutline style={{ color: '#389E0D', fontSize: '18px' }} />,
            })),
            ...activitiesData
              .map(activity => {
                // Special handling for chatMessages and aiActivityResult
                if (
                  activity.iconType === 'chatMessages' ||
                  activity.iconType === 'chatMessage' ||
                  activity.iconType === 'aiActivityResult'
                ) {
                  // Replicate the title logic from MessageItem
                  const activity_associate_name = activity.activity_associate_name || activity.associate_name;
                  const activity_date = activity.activity_date;
                  const activity_type = activity.activity_type;
                  let title = null;
                    if (activity_type === 'aiActivityResult') {
                      title = (
                        <>
                          <span className="fw-600">{activity_associate_name || 'An Unknown Associate'} </span>did an AI Insight
                          on
                          <span className="fw-600"> {moment(activity_date).format('MMM DD, YYYY')}</span>
                        </>
                      );
                    } else {
                      title = (
                        <>
                          <span className="fw-600">{activity_associate_name || 'An Unknown Associate'} </span>sent a message on
                          <span className="fw-600"> {moment(activity_date).format('MMM DD, YYYY')}</span>
                        </>
                      );
                    }
                  if (!title) return null;
                  return {
                    children: <TimelineItem title={title} description={activity.msg} />,
                    dot: Icon(activity.iconType),
                  };
                }
                // Default handling for other types
                const child = itemSwitch(activity);
                if (!child) return null;
                return {
                  children: child,
                  dot: Icon(activity.iconType),
                };
              })
              .filter(Boolean),
          ]}
        />
      </div>
    </div>
  );
};
export default SalesExpandRow;

const Icon = type => {
  switch (type) {
    case 'reminder':
      return <BellOutlined style={{ opacity: 0.45, color: '#000' }} />;
    case 'massMessage':
    case 'autoMessage':
    case 'chatMessage':
      return <MassMessages style={{ fontSize: '16px', opacity: 0.45 }} />;
    case 'aiActivityResult':
      return <BulbOutlined style={{ opacity: 0.45, color: '#000' }} />;
    case 'clientActivity':
      return <ClientActivity style={{ opacity: 0.75, color: '#000', fontSize: '16px' }} />;
    case 'appointments':
      return <CalendarOutlined style={{ opacity: 0.45, color: '#000' }} />;
    default:
      return null;
  }
};

const MessageItem = ({ activity_associate_name, activity_date, activity_type, msg }) => {
  const title = useMemo(() => {
    return activity_type === 'aiActivityResult' ? (
      <>
        <span className="fw-600">{activity_associate_name || 'An Unknown Associate'} </span>did an AI Insight on
        <span className="fw-600"> {moment(activity_date).format('MMM DD, YYYY')}</span>
      </>
    ) : (
      <>
        <span className="fw-600">{activity_associate_name || 'An Unknown Associate'} </span>sent a message on
        <span className="fw-600"> {moment(activity_date).format('MMM DD, YYYY')}</span>
      </>
    );
  }, [activity_associate_name]);

  // Prevent display of client inbound messages
  if (!title) return null;

  return <TimelineItem title={title} description={msg} />;
};

const ClientActivityItem = activity => {
  const { activity_associate_name, created_date, activity_type, notes } = activity;
  const title = useMemo(() => {
    return (
      <>
        <span className="fw-600">{activity_associate_name || 'An Unknown Associate'} </span>
        did a {activity_type} activity on
        <span className="fw-600"> {moment(created_date, 'YYYYMMDDHHmmss').format('MMM DD, YYYY')}</span>
      </>
    );
  }, [activity_associate_name, activity_type]);

  return <TimelineItem title={title} description={notes} />;
};

const AppointmentItem = activity => {
  const { activity_associate_name, client_name, activity_date, appointment_type_name, appointment_note, appointment_date } = activity;
  const title = useMemo(() => {
    return (
      <>
        <span className="fw-600">{client_name} </span>
        scheduled a {appointment_type_name || 'appointment'} on
        <span className="fw-600"> {moment(appointment_date).format('MMM DD, YYYY')} at {moment(appointment_date).format('hh:mm A')}</span>
      </>
    );
  }, [activity_associate_name, appointment_type_name, activity_date]);

  return <TimelineItem title={title} description={appointment_note} />;
};
